import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowBranchFlow from './components/WorkflowBranchFlow';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowOutputBox from './components/WorkflowOutputBox';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowProcedureFlow from './components/WorkflowProcedureFlow';
import WorkflowSection from './components/WorkflowSection';
import WorkflowSeriesComplete from './components/WorkflowSeriesComplete';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';

const reqSteps = [
  { label: 'Part1', title: 'RFPを作成する', path: '/devdocs/workflow/req-step1' },
  { label: 'Part2', title: 'AI対話で仕様を策定する', path: '/devdocs/workflow/req-step2' },
  { label: 'Part3', title: 'proposal/businessを出力する', path: '/devdocs/workflow/req-step3' },
  {
    label: 'Part4',
    title: 'システムユースケースを出力する',
    path: '/devdocs/workflow/req-step4',
    active: true,
  },
];

const WorkflowReqStep4: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="要件定義編 Part4 — システムユースケースを出力する"
      seriesName="要件定義編"
      tags={['Requirements', 'usecases']}
      seriesSteps={reqSteps}
      prevLink={{ label: 'Part3: proposal/business', path: '/devdocs/workflow/req-step3' }}
      nextLink={{
        label: 'バックエンド開発編 Part1: スキーマ定義とAPI仕様書',
        path: '/devdocs/workflow/be-step1',
      }}
    >
      {/* この記事で学べること */}
      <WorkflowLearningPoints
        items={[
          'usecases.md（システムユースケース）の役割とbusiness.mdとの違い',
          '1UC = 1API を基本としたUC粒度の設計',
          'エラーケースの網羅とDB操作レベルの結果記述',
          '段階的生成による品質安定化',
          '要件定義フェーズ全体の成果物の関係',
        ]}
      />

      {/* 目次 */}
      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '概要' },
          { id: 'section-2', label: '成果物' },
          { id: 'section-3', label: 'business.mdとの違い' },
          { id: 'section-4', label: '出力手順' },
          { id: 'section-5', label: '修正指示の出し方' },
          { id: 'section-6', label: '後続工程での利用方法' },
          { id: 'section-7', label: 'よくあるミスとリカバリ' },
          { id: 'section-8', label: '提出前チェックリスト' },
          { id: 'section-9', label: 'ベストプラクティス' },
          { id: 'section-10', label: 'アンチパターン' },
          { id: 'section-11', label: '成果物まとめ' },
        ]}
      />

      {/* 1. 概要 */}
      <WorkflowSection title="概要" id="section-1" number={1}>
        <WorkflowTable
          headers={['項目', '内容']}
          rows={[
            [
              '前提条件',
              'Part3が完了し、proposal.md と business.md が生成されている。Part3の「提出前チェックリスト」に全項目合格している',
            ],
            [
              '完了条件',
              'usecases.md が生成されている。提出前チェックリストの全項目に合格している',
            ],
          ]}
        />
        <WorkflowText>
          Part3で作成した proposal.md（技術仕様）と
          business.md（ユーザー視点のシナリオ）を統合し、API単位で技術的に再定義した文書を出力します。これが要件定義フェーズの最終成果物であり、バックエンド開発と結合試験の直接のインプットになります。
        </WorkflowText>

        {/* 3段構成フローチャート */}
        <Stack spacing={1} sx={{ mt: 2 }}>
          {/* 入力（Part3の成果物） */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1, display: 'block' }}
            >
              入力（Part3の成果物）
            </Typography>
            <WorkflowBranchFlow
              input={{ label: 'proposal.md + business.md', sub: 'Part3で確定済み' }}
              outputs={[
                { label: 'proposal.md', sub: 'テーブル定義 / API一覧・概要 / ビジネスルール' },
                { label: 'business.md', sub: 'ユーザー操作手順 / 入力項目・注意事項' },
              ]}
            />
          </Box>

          {/* Part4 出力 */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1, display: 'block' }}
            >
              Part4 出力
            </Typography>
            <WorkflowBranchFlow
              input={{
                label: 'usecases.md',
                sub: '1UC = 1API を基本に技術的に再定義 / DB操作レベルの結果記述 / 全エラーケースをHTTPステータス付きで網羅',
                highlight: true,
              }}
              outputs={[
                { label: 'proposal.md', sub: 'テーブル定義・API一覧' },
                { label: 'business.md', sub: 'ユーザー操作手順' },
              ]}
              reverse
            />
          </Box>

          {/* 後続工程での利用先 */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1, display: 'block' }}
            >
              後続工程での利用先
            </Typography>
            <Stack spacing={2}>
              <WorkflowBranchFlow
                input={{
                  label: 'バックエンド開発',
                  sub: 'スキーマ定義 / API仕様書 / DD書・実装',
                  highlight: true,
                }}
                outputs={[{ label: 'usecases.md' }, { label: 'proposal.md' }]}
                reverse
              />
              <WorkflowBranchFlow
                input={{
                  label: 'テスト',
                  sub: 'E2Eテスト（正常系・異常系） / 結合試験（業務シナリオ検証）',
                  highlight: true,
                }}
                outputs={[{ label: 'usecases.md' }, { label: 'business.md' }]}
                reverse
              />
            </Stack>
          </Box>
        </Stack>

        {/* なぜ usecases.md が必要か */}
        <WorkflowInfoBox type="important">
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            なぜ usecases.md が必要か — business.md だけではバックエンド開発に使えない
          </Typography>
          <WorkflowText sx={{ mb: 1 }}>
            business.md はユーザー視点、usecases.md
            はシステム視点。視点が違えばファイルを分ける。business.md
            は「ユーザーが画面から何をするか」を記述した文書です。バックエンド開発者にとっては以下の情報が不足しています：
          </WorkflowText>
          <WorkflowTable
            headers={['不足する情報', '具体例']}
            rows={[
              ['DB操作の詳細', 'どのテーブルにINSERT/UPDATE/DELETEするのか'],
              ['全エラーケース', '400/401/403/404/422 の全パターンとその条件'],
              [
                'APIフィールド名',
                '画面のラベル名ではなく、リクエスト/レスポンスの技術的なフィールド名',
              ],
              ['ステータス遷移', 'どの状態からどの状態に遷移するか'],
              ['排他制御', '同時操作時の動作'],
            ]}
          />
          <WorkflowText sx={{ mt: 1, mb: 1 }}>
            usecases.md は、これらの情報を 1UC = 1API
            を基本とした粒度で補完した文書です。ただし、1APIに対して複数UCが紐づくケースもあります：
          </WorkflowText>
          <WorkflowTable
            headers={['ケース', '例']}
            rows={[
              [
                'アクターが異なる',
                '同じ取得APIでも「管理者が全件取得」と「一般ユーザーが自分のデータのみ取得」で前提条件・結果が異なる',
              ],
              [
                '前提条件（ステータス）が異なる',
                '同じ更新APIでも「下書き状態からの更新」と「承認済み状態からの更新」で手順・エラーケースが異なる',
              ],
              [
                '操作の意味が異なる',
                '同じ更新APIでも「情報の修正」と「契約終了日の設定（実質的な論理削除）」で業務上の意味が異なる',
              ],
            ]}
          />
          <WorkflowText sx={{ mt: 1 }}>
            迷ったときの判断基準：テストシナリオとして別ケースになるなら、UCも分ける。
          </WorkflowText>
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* 2. 成果物: usecases.md */}
      <WorkflowSection title="成果物: *.usecases.md" id="section-2" number={2}>
        <WorkflowInfoBox>
          usecases.md
          は「要件定義」と「基本設計」の橋渡しをする文書です。ビジネスユースケース（ユーザーが何をしたいか）を、システムが実際にどう動くか（API単位のDB操作・エラーハンドリング）に翻訳したもの。バックエンド開発者はこのファイルを見れば「何を実装すればよいか」が分かる状態を目指します。
        </WorkflowInfoBox>
        <WorkflowTable
          headers={['項目', '内容']}
          rows={[
            ['配置先', '.kiro/requirements/usecases/'],
            ['ファイル名', '{機能名}.usecases.md'],
            ['例', '.kiro/requirements/usecases/partner.usecases.md'],
          ]}
        />

        <WorkflowSection
          title="Steeringに定義するフォーマット（本プロジェクトの例）"
          subNumber="2.1"
        >
          <WorkflowText sx={{ mb: 1 }}>
            usecases.md
            は人間が手書きするものではなく、AIが自動生成します。出力フォーマットを揃えるために、Steeringファイル（create_usecases.md）に以下のような構造を定義しています。Front
            Matterは fileMatch で .kiro/requirements/**/*.proposal.md、
            .kiro/requirements/usecases/*.business.md、 .kiro/requirements/usecases/*.usecases.md
            を指定します。AIはこのSteeringを参照して usecases.md
            を生成するため、毎回同じ構造で出力されます。
          </WorkflowText>
          <WorkflowCodeBlock
            title="usecases.md フォーマット（Steering定義）"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# {機能名} ユースケース一覧

## アクター定義

| アクター | 説明 | 権限 |
|----------|------|------|
| ...      | ...  | ...  |

## {区分名}（マスタ区分がある場合）

| 区分コード | 名称 |
|------------|------|
| ...        | ...  |

---

## {アクター名}ユースケース

### UC-{PREFIX}{連番}: {ユースケース名}

- 操作者: {権限}
- API: {API_ID} {API名} \`{Method} {Endpoint}\`
- 前提条件: {前提}
- 手順:
  1. {操作1}
  2. {操作2}
  3. ...
- 結果: {DB操作レベルの具体的な結果}
- 備考:
  - {補足事項}
- エラーケース:
  - {条件} → {HTTPステータス}（{エラー種別}）

---

## ビジネスルール

### {ルール名}

- {ルールの詳細}`}
          />

          <WorkflowInfoBox type="warning">
            このフォーマットはプロジェクトによって変わります。GraphQL
            APIならQuery/Mutation単位、イベント駆動なら「イベント発行」、マイクロサービスならサービス間呼び出し手順が加わります。自分のプロジェクトに合わせてSteeringのフォーマットをカスタマイズしてください。
          </WorkflowInfoBox>
        </WorkflowSection>

        {/* Steeringに定義すべき記載ルール */}
        <WorkflowSection
          title="Steeringに定義すべき記載ルール（本プロジェクトの例）"
          subNumber="2.2"
        >
          <WorkflowText sx={{ mb: 1 }}>
            テンプレート構造だけでなく、AIが守るべき記載ルールもSteeringに定義しておきます。
          </WorkflowText>
          <WorkflowCodeBlock
            title="usecases記載ルール（Steering定義例）"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`## 記載ルール

### 結果
- DB操作レベルで具体的に記述する（テーブル名+INSERT/UPDATE/DELETE）
- 自動設定される項目（created_at, UUID等）を明記する
- レスポンスで返却される項目があれば記載する

### エラーケース
- 全エラーパターンをHTTPステータスコード付きで網羅する
- 以下の順序で列挙する: 400 → 401 → 403 → 404 → 422

### 手順
- business.md の手順をベースに、APIフィールド名で記述する
- master_type を使用するフィールドは（master_type: {type_name}）と明記する

### ビジネスルール
- proposal.md のビジネスルールをシステム視点で整理して転記する
- 独自に追加・改変しない

## 禁止事項
- proposal.md に存在しないAPIのUCを作成しない
- エラーケースを省略しない（特に401/403は全UCで必須）
- 結果を曖昧に書かない（「登録される」ではなく「{テーブル名}にINSERTされる」）
- ビジネスルールを独自に追加しない`}
          />
        </WorkflowSection>

        {/* 各セクションの狙い */}
        <WorkflowSection title="各セクションの狙い" subNumber="2.3">
          <WorkflowTable
            headers={['セクション', '狙い', '後続工程での使われ方']}
            rows={[
              [
                'アクター定義',
                '操作権限の異なるユーザーグループを一覧化',
                'SecurityConfig・認可チェックの設計根拠',
              ],
              [
                '区分定義',
                'master_typeの区分コード一覧を明示',
                'Enum定義・バリデーション実装の入力',
              ],
              ['操作者', '各UCに必要な権限を明確化', 'SessionFilterの認可チェック実装'],
              ['API', '1UC = 1API の対応を明示', 'API仕様書のRequest/Response設計'],
              ['前提条件', '操作実行に必要なシステム状態', 'テストのprecondition、排他制御の設計'],
              ['手順', 'APIフィールド名レベルの技術手順', 'UseCase/Serviceの処理フロー設計'],
              ['結果', 'DB操作レベルの具体的な結果', 'テストのassertion、Mapper SQLの設計'],
              ['備考', '操作上の補足・ビジネスルールへの言及', 'DD書の補足説明、エッジケース対応'],
              [
                'エラーケース',
                '全エラーパターンをHTTPステータス付きで網羅',
                'ExceptionHandler設計、異常系テスト設計',
              ],
              [
                'ビジネスルール',
                'ドメイン固有のルールをシステム視点で整理',
                'UseCase/Service内のビジネスロジック実装',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* 3. business.md との違い */}
      <WorkflowSection title="business.md との違い" id="section-3" number={3}>
        <WorkflowText sx={{ mb: 1 }}>
          usecases.md の記述粒度を理解するために、business.md との違いを比較します。
        </WorkflowText>
        <WorkflowTable
          headers={['観点', 'business.md', 'usecases.md']}
          rows={[
            ['視点', 'ユーザー（画面操作）', 'システム（API/DB操作）'],
            ['手順', '画面操作の手順', 'API呼び出しの技術手順（フィールド名で記述）'],
            [
              '結果',
              '「登録される」「表示される」',
              '「partnerテーブルに1件INSERT」「UUID自動採番」',
            ],
            [
              'エラー',
              '記載なし or 注意事項として簡易記載',
              '全エラーケースをHTTPステータス付きで列挙',
            ],
            [
              '入力',
              '画面項目名（テキスト/プルダウン）',
              'APIフィールド名（型/master_type参照先）',
            ],
            [
              '粒度',
              '1BUC = 1目的（複数API可）',
              '1UC = 1API（基本。アクターや前提条件が異なれば1APIに複数UC）',
            ],
            ['利用目的', '画面実装、操作手順書', 'バックエンド開発、結合試験'],
          ]}
        />

        {/* 実際の例（パートナー登録） */}
        <WorkflowSection title="実際の例（パートナー登録）" subNumber="3.1">
          <WorkflowText sx={{ mb: 1 }}>
            同じ機能でも、business.md と usecases.md で記述レベルがどう変わるかを示します。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            business.md での記述
          </Typography>
          <WorkflowCodeBlock
            language="markdown"
            collapsible
            defaultCollapsed={false}
            code={`## BUC-P01: 新規パートナーを登録する

- 目的: 新しいパートナー企業をシステムに登録する
- 操作者: 管理者（ADMIN/BACK_OFFICE/MANAGER権限）
- 前提条件: ログイン済み
- 利用API:
  - API3001 パートナー登録API \`POST /v1/partner/create\`（登録実行）
- 手順:
  1. パートナー管理画面を開く。
  2. 「新規登録」ボタンを押下して登録フォームを開く。
  3. パートナー名、パートナー名カナ、パートナー区分、契約開始日を入力する。
  4. 「登録」ボタンを押下する。
- 入力項目:
  | 項目名 | 必須/任意 | 形式 | 桁数 | 備考 |
  |--------|-----------|------|------|------|
  | パートナー名 | 必須 | テキスト | 200 | - |
  | パートナー名カナ | 必須 | テキスト | 200 | - |
  | パートナー区分 | 必須 | プルダウン | - | 法人/個人事業主 |
  | 契約開始日 | 必須 | 日付 | - | - |
- 結果: パートナーが登録される。パートナーIDが自動的に発行される。
- 注意事項: 登録後にパートナーIDが発行される。一度登録すると削除はできない。`}
          />

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
            usecases.md での記述
          </Typography>
          <WorkflowCodeBlock
            language="markdown"
            collapsible
            defaultCollapsed={false}
            code={`### UC-P01: パートナーを登録する

- 操作者: 管理者（ADMIN/BACK_OFFICE/MANAGER権限）
- API: API3001 パートナー登録API \`POST /v1/partner/create\`
- 前提条件: ログイン済み、ADMIN/BACK_OFFICE/MANAGER権限あり
- 手順:
  1. パートナー管理画面を開く。
  2. 「新規登録」ボタンを押下して登録フォームを開く。
  3. 以下の必須項目を入力する。
     - partner_name（パートナー名）
     - partner_name_kana（パートナー名カナ）
     - partner_type_id（パートナー区分: master_type: partner_type）
     - contract_start_date（契約開始日）
  4. 必要に応じて任意項目を入力する。
  5. 「登録」ボタンを押下する。
- 結果: partnerテーブルに新規レコードが1件作成される。
         パートナーIDはUUID（64桁）で自動採番され、レスポンスとして返却される。
         登録日（created_at）・登録者（created_by）は自動設定される。
- エラーケース:
  - 必須項目未入力 → 400（バリデーションエラー）
  - partner_name が200文字超 → 400（バリデーションエラー）
  - partner_type_id が master_type に存在しない → 400（バリデーションエラー）
  - セッションが存在しない/期限切れ → 401（認証エラー）
  - 権限が足りない → 403（認可エラー）`}
          />
        </WorkflowSection>

        {/* 何が変わったか */}
        <WorkflowSection title="何が変わったか" subNumber="3.2">
          <WorkflowTable
            headers={['変換ポイント', 'business.md', 'usecases.md']}
            rows={[
              ['入力項目名', '「パートナー名」', 'partner_name（APIフィールド名）'],
              ['区分の指定方法', '「法人/個人事業主から選択」', 'master_type: partner_type'],
              ['結果の具体性', '「登録される」', '「partnerテーブルにINSERT、UUID自動採番」'],
              ['エラーの有無', '記載なし', '400/401/403 を全パターン列挙'],
              ['自動設定項目', '記載なし', '「created_at, created_by は自動設定」'],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* 4. 出力手順 */}
      <WorkflowSection title="出力手順" id="section-4" number={4}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          全体フロー
        </Typography>

        {/* ビジュアルフロー図 */}
        <WorkflowProcedureFlow
          steps={[
            { label: 'Part3', variant: 'external' },
            {
              label: 'usecases.md を生成',
              sub: '確認: 全APIにUCが存在するか / エラーケースが全パターン網羅されているか',
            },
            {
              label: '整合性チェック',
              sub: 'proposal の API一覧とUCの一致 / business の BUCとUCの対応 / ビジネスルールの整合',
              variant: 'check',
            },
            { label: '提出前チェックリストの確認', variant: 'complete' },
            { label: 'バックエンド開発編', variant: 'external' },
          ]}
        />

        {/* どちらの指示方法を使うか */}
        <WorkflowSection title="どちらの指示方法を使うか" subNumber="4.1">
          <WorkflowText sx={{ mb: 1 }}>
            Steeringファイルにフォーマット・記載ルール・禁止事項が定義済みのため、基本的にはシンプルな指示で正しく生成されます。ただし、機能が複雑な場合はうまく生成されないことがあります。
          </WorkflowText>
          <WorkflowTable
            headers={['条件', '使う方法', '理由']}
            rows={[
              ['APIが5個以下、ビジネスルールが単純', '一括生成', 'Steeringだけで品質が安定する'],
              [
                'APIが6個以上、またはビジネスルールが複雑',
                '段階的生成',
                '入力ファイルのボリュームが大きいとコンテキストが圧迫され、後半のUCの品質が下がる',
              ],
              [
                '初めてusecases.mdを生成する',
                '段階的生成',
                '生成結果を1ブロックずつ確認しながら進められるので学習効果が高い',
              ],
            ]}
          />
        </WorkflowSection>

        {/* 一括生成パターン */}
        <WorkflowSection title="指示方法: 一括生成パターン" subNumber="4.2">
          <WorkflowText sx={{ mb: 1 }}>
            機能がシンプルな場合の指示例です。Steeringにルールが定義済みなので、指示はシンプルでOKです。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            #partner.proposal.md と #partner.business.md を元に、{'\n'}
            .kiro/requirements/usecases/partner.usecases.md（システムユースケース）を生成してください。
          </WorkflowChatBubble>
        </WorkflowSection>

        {/* 段階的生成パターン */}
        <WorkflowSection title="指示方法: 段階的生成パターン" subNumber="4.3">
          <WorkflowText sx={{ mb: 1 }}>
            機能が複雑な場合（APIが多い、ビジネスルールが多い）の指示例です。アクターやサブグループ単位に分割して生成することで、各UCの品質を安定させます。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            Step A: アクター定義と区分定義を先に出力
          </Typography>
          <WorkflowChatBubble role="human">
            #partner.proposal.md を元に、partner.usecases.md の{'\n'}
            アクター定義と区分定義のセクションだけを出力してください。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            Step B: UCを追加
          </Typography>
          <WorkflowChatBubble role="human">
            #partner.proposal.md と #partner.business.md を元に、{'\n'}
            管理者ユースケースの全UCを追加してください。{'\n\n'}
            全APIについて1UC作成し、エラーケースは全パターン網羅してください。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            Step C: ビジネスルールを追加
          </Typography>
          <WorkflowChatBubble role="human">
            最後にビジネスルールセクションを追加してください。{'\n'}
            proposal.md のビジネスルールをシステム視点で整理して転記してください。
          </WorkflowChatBubble>
        </WorkflowSection>

        {/* 生成後の確認 */}
        <WorkflowSection title="生成後の確認" subNumber="4.4">
          <WorkflowText sx={{ mb: 1 }}>
            生成が完了したら、AIに整合性チェックを依頼します。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            生成した partner.usecases.md について以下を確認してください：{'\n\n'}
            1. proposal.md の全APIに対してUCが存在するか{'\n'}
            2. 全UCのエラーケースに401（認証）と403（認可）が含まれているか{'\n'}
            3. business.md の全BUCが少なくとも1つのUCと対応しているか{'\n'}
            4. 結果がDB操作レベル（テーブル名+操作種別）で記述されているか{'\n'}
            5. ビジネスルールが proposal.md と整合しているか{'\n\n'}
            過不足があれば修正してください。
          </WorkflowChatBubble>
          <WorkflowInfoBox>
            AIに「proposalとusecasesの整合性をチェックして」と指示すれば、APIの抜け漏れやエラーケースの不足をAIが自動検出してくれます。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 5. 修正指示の出し方 */}
      <WorkflowSection title="修正指示の出し方" id="section-5" number={5}>
        <WorkflowText sx={{ mb: 1 }}>
          生成された usecases.md に修正が必要な場合、Step3と同じ2つの方法が使えます。
        </WorkflowText>
        <WorkflowInfoBox>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            ポイント: どちらの方法を使うべきか
          </Typography>
          <WorkflowTable
            headers={['方法', '向いているケース']}
            rows={[
              [
                'チャットで指示',
                '修正が1〜2箇所。「全UCのエラーケースに422を追加して」のような一括指示',
              ],
              ['ファイル内注釈', '修正箇所が3箇所以上に散在し、各UCで異なる修正内容がある場合'],
            ]}
          />
        </WorkflowInfoBox>

        <WorkflowSection title="チャットでの修正指示例" subNumber="5.1">
          <WorkflowChatBubble role="human">
            partner.usecases.md を確認しました。以下を修正してください：{'\n'}- UC-P01
            のエラーケースに「partner_name_kana が200文字超 → 400」を追加して{'\n'}- UC-P02
            の結果に「updated_at, updated_by が自動更新される」を追記して{'\n'}-
            ビジネスルールの契約終了判定の説明を proposal.md と一致させて
          </WorkflowChatBubble>
        </WorkflowSection>

        <WorkflowSection title="ファイル内注釈（note記法）での修正指示" subNumber="5.2">
          <WorkflowText sx={{ mb: 1 }}>
            修正箇所が複数に散在する場合は、ファイル内に直接注釈を書き込む方法が効果的です。note記法の詳細（定義方法・記法ルール・注意点）は
            <Link
              component={RouterLink}
              to="/devdocs/workflow/req-step3#section-5"
              underline="hover"
            >
              Step3 セクション5「修正指示の出し方」
            </Link>
            を参照してください。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            partner.usecases.md に注釈を書き込みました。{'\n'}
            全てのnoteに対応して修正し、注釈は削除してください。
          </WorkflowChatBubble>
        </WorkflowSection>
      </WorkflowSection>

      {/* 6. 後続工程での利用方法 */}
      <WorkflowSection title="後続工程での利用方法" id="section-6" number={6}>
        <WorkflowText sx={{ mb: 1 }}>usecases.md は以下の後続工程で利用されます。</WorkflowText>
        <WorkflowTable
          headers={['利用先', 'usecases.md のどこを使うか', '具体的な使い方']}
          rows={[
            [
              'スキーマ定義作成',
              '結果セクション（テーブル名・自動設定項目）',
              'カラム・制約・インデックスの根拠',
            ],
            [
              'API仕様書作成',
              '手順（フィールド名）・結果・エラーケース',
              'Request/Response/処理概要の根拠',
            ],
            [
              'DD書（詳細設計）作成',
              '手順・結果・ビジネスルール',
              'UseCase/Service/Repository の処理フロー設計',
            ],
            ['E2Eテスト', '手順（正常系）+ エラーケース（異常系）', 'テストケース設計のベース'],
            ['結合試験', '全UC + ビジネスルール', '業務シナリオの検証項目'],
          ]}
        />

        <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
          バックエンド開発チームの利用例
        </Typography>
        <WorkflowCodeBlock
          language="text"
          code={`usecases.md
    │
    ├── Step1: スキーマ定義（テーブル定義の根拠）
    │     └── 結果セクションの「xxxテーブルにINSERT」から必要カラムを確認
    │
    ├── Step1: API仕様書（Request/Response設計）
    │     └── 手順セクションのフィールド名 → Requestフィールド
    │     └── 結果セクションの返却項目 → Responseフィールド
    │     └── エラーケース → エラーレスポンス定義
    │
    └── Step2以降: DD書・実装
          └── 手順 → UseCase処理フロー
          └── ビジネスルール → Service実装`}
        />

        <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
          テストチームの利用例
        </Typography>
        <WorkflowCodeBlock
          language="text"
          code={`usecases.md
    │
    ├── 正常系テスト: 各UCの「手順」→「結果」を1テストケースとする
    │
    ├── 異常系テスト: 各UCの「エラーケース」を1テストケースとする
    │
    └── シナリオテスト: 複数UCを組み合わせた業務フロー
          例: UC-P01（登録）→ UC-P02（更新）→ UC-P04（取得して確認）`}
        />
      </WorkflowSection>

      {/* 7. よくあるミスとリカバリ */}
      <WorkflowSection title="よくあるミスとリカバリ" id="section-7" number={7}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          よくあるミス（AI生成時に起きやすい問題）
        </Typography>
        <WorkflowInfoBox type="warning">
          <WorkflowTable
            headers={['AIがやりがちなこと', '何が起きるか', '防止策']}
            rows={[
              [
                'エラーケースの401/403を省略する',
                '結合試験でセキュリティ系テストが漏れる',
                '「全UCに401と403が含まれているか確認して」と指示する',
              ],
              [
                '結果を曖昧に書く（「登録される」）',
                'バックエンド開発者がテーブル名・操作種別を特定できない',
                '「結果はテーブル名+INSERT/UPDATE/DELETEで記述して」と指示する',
              ],
              [
                'business.md の手順をそのままコピーする',
                'APIフィールド名に変換されず、画面ラベル名のままになる',
                '「手順はAPIフィールド名で記述して」と指示する',
              ],
              [
                'proposal.md にないAPIのUCを勝手に作る',
                '決定していない仕様が混入する',
                '「proposalのAPI一覧に存在するAPIのみUCを作成して」と指示する',
              ],
              [
                'ビジネスルールを独自に追加・改変する',
                'proposal.md と食い違い、後工程で矛盾が発生する',
                '「ビジネスルールはproposalから転記のみ。追加しないで」と指示する',
              ],
              [
                'UC-IDの連番が飛ぶ・重複する',
                'テストケースとの対応が崩れる',
                '生成後に「UC-IDの連番に飛びや重複がないか確認して」と指示する',
              ],
              [
                'master_type の記載を省略する',
                'APIフィールドがどの区分を参照するか不明になる',
                '「master_typeを使うフィールドは（master_type: {type_name}）と明記して」と指示する',
              ],
            ]}
          />
        </WorkflowInfoBox>

        {/* リカバリ手順 */}
        <WorkflowSection title="リカバリ手順" subNumber="7.1">
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            proposal.md が修正された場合
          </Typography>
          <WorkflowText sx={{ mb: 0.5 }}>
            proposalが修正されたら、usecases.md も連動修正が必要です。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            partner.proposal.md のAPI一覧に API3006 が追加されました。{'\n'}
            partner.usecases.md に対応するUCを追加してください。{'\n'}
            エラーケースは全パターン網羅してください。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            business.md が修正された場合
          </Typography>
          <WorkflowText sx={{ mb: 0.5 }}>
            businessの手順や入力項目が変わった場合、UCの手順も更新が必要です。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            partner.business.md の BUC-P01 の入力項目に「備考」が追加されました。{'\n'}
            partner.usecases.md の UC-P01 の手順に反映してください。{'\n'}
            任意項目として追加し、フィールド名は proposal.md のテーブル定義に合わせてください。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            生成結果が大きくズレている場合
          </Typography>
          <WorkflowText sx={{ mb: 0.5 }}>
            AIの出力が期待と大きく異なる場合は、Steeringのテンプレートを明示的に渡して再生成します。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            出力がSteeringのテンプレートと合っていないので、再生成してください。{'\n'}
            create_usecases.md のテンプレート構造に従って出力して。{'\n'}
            特に以下を守ってください：{'\n'}- 1API = 1UC{'\n'}- エラーケースは 400 → 401 → 403 → 404
            → 422 の順{'\n'}- 結果はDB操作レベル（テーブル名+操作種別）
          </WorkflowChatBubble>
        </WorkflowSection>
      </WorkflowSection>

      {/* 8. 提出前チェックリスト */}
      <WorkflowSection title="提出前チェックリスト" id="section-8" number={8}>
        <WorkflowInfoBox type="important">
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            AIが生成したドキュメントを人の目でチェックする理由
          </Typography>
          <WorkflowTable
            headers={['理由', '具体的に何が起きるか']}
            rows={[
              [
                'エラーケースの漏れが試験漏れに直結する',
                '401/403を省略したUCがあると、セキュリティテストで発覚する。発覚が遅いほど修正コストが上がる',
              ],
              [
                'proposal と usecases の食い違いがバックエンド実装を混乱させる',
                'APIの追加・変更がusecasesに反映されていないと、開発者が古い仕様で実装を進めてしまう',
              ],
              [
                'ビジネスルールの独自追加が仕様のブレを生む',
                'AIがproposalにない独自ルールを追加すると「誰が決めた？」問題が発生する',
              ],
              [
                '品質の最終責任は人間にある',
                'usecases.md は要件定義の最終成果物。ここの品質が後続全工程に影響する',
              ],
            ]}
          />
          <WorkflowText sx={{ mt: 1 }}>
            usecases.md
            は要件定義フェーズの最終成果物です。ここを丁寧に確認するかどうかで、バックエンド開発以降の品質が決まります。
          </WorkflowText>
        </WorkflowInfoBox>

        <WorkflowInfoBox type="warning">
          以下はあくまで本プロジェクト（Java / REST API /
          MySQL）向けのチェックリストの例です。プロジェクトの技術スタック・ドキュメント体系によってチェック項目は変わります。自分のプロジェクト用のチェックリストを作成してください。
        </WorkflowInfoBox>

        <WorkflowText sx={{ mt: 1 }}>
          usecases.md の作成が完了したら、バックエンド開発に進む前に以下を確認してください。
        </WorkflowText>

        <WorkflowChecklist
          groups={[
            {
              title: '網羅性チェック（例）',
              items: [
                'proposal.md の全APIに対して1つ以上のUCが存在する',
                'business.md の全BUCが少なくとも1つのUCと対応している',
                '全UCのエラーケースに401（認証エラー）が含まれている（認証不要APIを除く）',
                '全UCのエラーケースに403（認可エラー）が含まれている（権限不問APIを除く）',
                'エラーケースの順序が 400 → 401 → 403 → 404 → 422 になっている',
              ],
            },
            {
              title: '記述レベルチェック（例）',
              items: [
                '結果がDB操作レベル（テーブル名 + INSERT/UPDATE/DELETE）で記述されている',
                '自動設定項目（created_at, updated_at, UUID等）が結果に明記されている',
                '手順がAPIフィールド名で記述されている（画面ラベル名ではない）',
                'master_type を使うフィールドに（master_type: {type_name}）が明記されている',
                'UC-IDの連番に飛び・重複がない',
              ],
            },
            {
              title: '整合性チェック（例）',
              items: [
                'ビジネスルールが proposal.md の内容と一致している（独自追加がない）',
                'APIのMethod・Endpointが proposal.md のAPI一覧と一致している',
                'アクター定義の権限が proposal.md の認証・認可と一致している',
                '手順のフィールド名が proposal.md のテーブル定義のカラム名と対応している',
              ],
            },
          ]}
        />
      </WorkflowSection>

      {/* 9. ベストプラクティス */}
      <WorkflowSection title="ベストプラクティス" id="section-9" number={9}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: '生成後は必ずセクション8「提出前チェックリスト」を通す',
              effect: 'エラーケース漏れ・整合性不一致を次工程に持ち越さない',
            },
            {
              practice: '生成後にAIに「proposalとusecasesの整合性を確認して」と指示する',
              effect: 'APIの抜け漏れ・ビジネスルールの不一致をAIが自動検出してくれる',
            },
            {
              practice: 'APIの数が多い場合は段階的生成（アクター単位）で進める',
              effect: '一括生成だと後半のUCの品質が下がりやすい。分割すると精度が安定する',
            },
            {
              practice: 'エラーケースは 400 → 401 → 403 → 404 → 422 の順序を統一する',
              effect: '結合試験でエラーパターンを漏れなくテストしやすくなる',
            },
            {
              practice: '結果セクションに自動設定項目を必ず明記する',
              effect: 'バックエンド開発者がEntity/Mapperの自動設定を見落とさない',
            },
            {
              practice: 'proposal修正 → usecases連動修正を必ず行う',
              effect: '片方だけ直すと後工程でドキュメント間の矛盾が発覚する',
            },
            {
              practice: 'チェックリストの「整合性チェック」はAIに任せる',
              effect: '人間の目はパターンマッチが苦手。AIの方が漏れなく比較できる',
            },
            {
              practice: '提出前チェックリストの「網羅性チェック」は人の目でも確認する',
              effect: 'AIが全APIを網羅したと主張しても、proposal.mdと突き合わせて実際に確認する',
            },
          ]}
        />
      </WorkflowSection>

      {/* 10. アンチパターン */}
      <WorkflowSection title="アンチパターン" id="section-10" number={10}>
        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: '一括生成してレビューせずにバックエンド開発に進む',
              effect: 'エラーケース漏れが結合試験まで発覚せず、大幅手戻り',
            },
            {
              practice: 'エラーケースの401/403を「全UCに共通だから省略」とする',
              effect: 'テスト設計時に「このAPIは認証不要なのか？」と混乱する。省略禁止',
            },
            {
              practice: '結果を「登録される」「更新される」と曖昧に書く',
              effect: 'バックエンド開発者がどのテーブルの何を操作するか特定できない',
            },
            {
              practice: 'business.md の手順をそのままコピーする',
              effect: 'APIフィールド名に変換されず、後続のAPI仕様書作成時に再度変換作業が発生する',
            },
            {
              practice: 'proposal.md にないビジネスルールを独自に追加する',
              effect:
                '「誰が決めた？」問題。仕様の出所が不明になり、変更時に責任の所在が分からなくなる',
            },
            {
              practice: 'usecases.md を修正してproposal/businessを放置する',
              effect: 'usecases → proposal の逆方向の不整合が発生する。変更は常に上流から',
            },
            {
              practice: 'Steeringにフォーマットを定義せずに毎回プロンプトで指示する',
              effect: '生成のたびに構造がブレる。UC間でエラーケースの書き方が不統一になる',
            },
            {
              practice: '複雑な機能でも一括生成で済ませる',
              effect: 'APIが多い機能では後半のUCの品質が下がる。気づかずそのまま次工程に流れる',
            },
            {
              practice: 'チェックリストを形式的に流す',
              effect: 'usecasesはバックエンド開発の直接インプット。ここの漏れは即実装の漏れ',
            },
          ]}
        />
      </WorkflowSection>

      {/* 11. 成果物まとめ + 要件定義フェーズ完了 */}
      <WorkflowSection title="成果物まとめ" id="section-11" number={11}>
        <WorkflowOutputBox>
          <WorkflowTable
            headers={['成果物', '説明', '利用先']}
            rows={[
              [
                '*.usecases.md',
                'システムユースケース（API単位の技術仕様）',
                'バックエンド開発、E2Eテスト、結合試験',
              ],
            ]}
          />
        </WorkflowOutputBox>

        {/* 要件定義フェーズ完了 */}
        <Box
          sx={{
            border: 1,
            borderColor: 'success.main',
            borderRadius: 2,
            p: 2,
            mt: 2,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.06)' : 'rgba(76, 175, 80, 0.04)',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Chip label="Complete" size="small" color="success" />
            <Typography variant="body2" fontWeight={600}>
              要件定義フェーズ全4パート完了
            </Typography>
          </Stack>
          <WorkflowTable
            headers={['Part', '成果物', '内容']}
            rows={[
              ['Part1', '*.rfp.md', '機能のアイデア・草案'],
              ['Part2', '（対話記録）', 'AIとの仕様策定'],
              ['Part3', '*.proposal.md + *.business.md', '技術仕様 + ビジネスユースケース'],
              ['Part4', '*.usecases.md', 'システムユースケース（要件定義の最終成果物）'],
            ]}
          />
        </Box>

        <WorkflowInfoBox type="important">
          usecases.md
          は要件定義の最終成果物です。ここから先（バックエンド開発）で仕様変更が発生した場合は、必ず
          usecases.md → business.md → proposal.md → rfp.md
          の順に遡って整合性を保ってください。下流で見つけた問題を上流に反映しないと、ドキュメント間の矛盾が蓄積します。
        </WorkflowInfoBox>
      </WorkflowSection>

      <WorkflowSeriesComplete
        seriesName="要件定義編"
        message="要件定義フェーズ全4パートが完了しました。次はバックエンド開発に進みましょう。"
        nextSeries={{ label: 'バックエンド開発編', path: '/devdocs/workflow/be-step1' }}
      />
    </WorkflowArticleLayout>
  );
};

export default WorkflowReqStep4;
